import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, User, Mail, Phone, Lock, Briefcase, Palmtree } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import Loader from "@/components/ui/loader";
import { useI18n } from '@/hooks/useI18n';
import { cn } from "@/components/utils/utils";
import GoogleRegisterButton from "./GoogleRegisterButton";

type Role = 'traveler' | 'travel_designer' | '';

const RegisterFormContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [role, setRole] = useState<Role>('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  });

  const { signUp } = useAuth();
  const { t } = useI18n();

  const roleSelected = role !== '';

  const validateForm = () => {
    const errors: string[] = [];

    if (!formData.firstName.trim()) errors.push(t('register.firstNameRequired'));
    if (!formData.lastName.trim()) errors.push(t('register.lastNameRequired'));
    if (!formData.email.trim()) errors.push(t('register.emailRequired'));
    if (!formData.phone.trim()) errors.push(t('register.phoneRequired'));
    if (!role) errors.push('Please select your account type.');
    if (!formData.password || formData.password.length < 6) {
      errors.push(t('register.passwordTooShort'));
    }

    if (errors.length > 0) {
      toast({
        title: t('register.validationErrorTitle'),
        description: errors.join(', '),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const metadata = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        role,
      };

      const { error } = await signUp(formData.email, formData.password, metadata);

      if (error) {
        let errorMessage = t('register.registrationFailed');

        if (error.message.includes("User already registered")) {
          errorMessage = t('register.accountExists');
        } else if (error.message.includes("Password should be at least")) {
          errorMessage = t('register.passwordTooWeak');
        } else if (error.message.includes("Invalid email")) {
          errorMessage = t('register.invalidEmail');
        }

        toast({
          title: t('register.registrationFailed'),
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: t('register.registrationCompletedTitle'),
          description: t('register.registrationCompletedDesc'),
        });
        setFormData({ firstName: '', lastName: '', email: '', phone: '', password: '' });
        setRole('');
      }
    } catch {
      toast({
        title: t('register.registrationErrorTitle'),
        description: t('register.registrationErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Role selector — always active, gates everything below */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">{t('common.role')}</Label>
        <div className="grid grid-cols-2 gap-3 pb-4">
          <button
            type="button"
            onClick={() => setRole(role === 'traveler' ? '' : 'traveler')}
            className={cn(
              "flex flex-col items-center gap-1 rounded-xl border-2 p-1.5 text-xs font-medium transition-all",
              role === 'traveler'
                ? "border-[#030A14] bg-[#030A14] text-white"
                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            )}
          >
            <Palmtree className="w-5 h-5" />
            {t('role.traveler')}
          </button>
          <button
            type="button"
            onClick={() => setRole(role === 'travel_designer' ? '' : 'travel_designer')}
            className={cn(
              "flex flex-col items-center gap-1 rounded-xl border-2 p-1.5 text-xs font-medium transition-all",
              role === 'travel_designer'
                ? "border-[#030A14] bg-[#030A14] text-white"
                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            )}
          >
            <Briefcase className="w-5 h-5" />
            {t('role.travelAdvisor')}
          </button>
        </div>
      </div>

      {/* Everything below is gated on role selection */}
      <div
        className={cn(
          "space-y-4 transition-opacity duration-500",
          !roleSelected && "opacity-20 pointer-events-none select-none"
        )}
      >
        {/* Google sign-up */}
        <GoogleRegisterButton role={role} />

        {/* Divider */}
        <div className="relative flex items-center gap-3">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 shrink-0">or fill out the form</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Manual registration form */}
        <form onSubmit={handleRegister} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">{t('profile.firstName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="h-9 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
                  placeholder={t('register.placeholderFirstName')}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">{t('profile.lastName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="h-9 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
                  placeholder={t('register.placeholderLastName')}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="registerEmail" className="text-sm font-medium text-gray-700">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="registerEmail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-9 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
                placeholder={t('register.placeholderEmail')}
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="phone" className="text-sm font-medium text-gray-700">{t('profile.phone')}</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-9 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
                placeholder={t('register.placeholderPhone')}
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-1 pb-2">
            <Label htmlFor="registerPassword" className="text-sm font-medium text-gray-700">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="registerPassword"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-9 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
                placeholder={t('register.placeholderPassword')}
                required
                disabled={isLoading}
                minLength={6}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:bg-[hsl(var(--button-primary-pressed))] disabled:bg-[hsl(var(--button-primary-disabled))] disabled:text-white px-4 py-2 w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200 h-11 lg:h-12"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader size="sm" className="mr-3" />
                {t('register.registering')}
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5 mr-2" />
                {t('register.register')}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default RegisterFormContent;
